/**
 * The snapshot *is* the client state.
 *
 * No store, no reducer, no local optimism: the server sends a redacted
 * `TableSnapshot` after every mutation, and the UI renders it. `events` on the
 * snapshot are advisory and drive animation only — nothing is ever derived from
 * them, so a reconnect (which arrives with `events: []`) renders correctly.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  ClientEvent,
  ServerEvent,
  type CardId,
  type BotLevel,
  type GameId,
  type Seat,
  type SessionInvalidPayload,
  type FeltTouchRelay,
  type GestureRelay,
  type Suit,
  type TableSnapshot,
  type TaggedEvent,
} from '@cards/shared'
import { getSocket } from './socket'
import type { Socket } from 'socket.io-client'
import { installActionLock, runGuarded } from './actionLock'
import { SnapshotGate } from './snapshots'
import { receiveFelt, receiveGesture, setFeltSink, setGestureSink } from '../table/gestures'
import {
  INITIAL_CONNECTION,
  canAct,
  canQueue,
  reduceConnection,
  type ConnectionState,
} from './connection'

export interface TableError {
  readonly code: string
  readonly message: string
}

export interface TableSend {
  createTable(gameId: GameId, config?: unknown): void
  joinTable(code: string, seat?: Seat): void
  leaveTable(): void
  chooseSeat(seat: Seat): void
  /** Seats a bot, or changes a lobby bot's level. */
  addBot(seat: Seat, level?: BotLevel): void
  removeBot(seat: Seat): void
  updateConfig(config: unknown): void
  /** Host, lobby only: allow coach mode for every human seat, or not. */
  setCoach(allowed: boolean): void
  /** Host, lobby only: list the table under "Open tables" for anyone, or not. */
  setListed(listed: boolean): void
  startMatch(): void
  /** After a match: in (or out) for another with the same seats. */
  rematchChoice(isIn: boolean): void
  call(call: unknown): void
  decide(decision: unknown): void
  play(seat: Seat, card: CardId, nominate?: Suit): void
  ready(): void
  proposeReplacement(seat: Seat): void
  voteReplacement(seat: Seat, approve: boolean): void
  reclaimSeat(): void
  resume(tableId?: string): void
  requestSnapshot(): void
}

export interface TableState {
  readonly snapshot: TableSnapshot | null
  /** Offline, kicked and dead-session, told apart. Every one of these used to
   *  render as nothing at all (VERDICT B7). */
  readonly connection: ConnectionState
  readonly error: TableError | null
  /** Events from the newest snapshot, for animation only. */
  readonly events: readonly TaggedEvent[]
  readonly send: TableSend
  clearError(): void
  /** The player has read "this table is over" and is going back to the lobby. */
  acknowledgeEnd(): void
}

export function useTable(): TableState {
  const [snapshot, setSnapshot] = useState<TableSnapshot | null>(null)
  const [error, setError] = useState<TableError | null>(null)
  const [connection, dispatch] = useReducer(reduceConnection, INITIAL_CONNECTION)
  // `send` is memoised once, so it reads the live connection through a ref
  // rather than closing over a stale copy of it.
  const connectionRef = useRef(connection)
  connectionRef.current = connection
  // Per-table watermark, so the second table of an evening is not discarded as
  // stale. The rule itself lives in `snapshots.ts`, where it is testable.
  const gate = useRef(new SnapshotGate())

  useEffect(() => {
    const socket = getSocket()
    installActionLock(socket)

    const onSnapshot = (next: TableSnapshot) => {
      if (gate.current.accept(next)) setSnapshot(next)
    }
    const onError = (e: TableError) => setError(e)
    const onSessionInvalid = (e: SessionInvalidPayload) =>
      dispatch({ type: 'sessionInvalid', payload: e })
    const onKicked = (e: { tableId?: string; reason?: string }) =>
      dispatch({
        type: 'kicked',
        reason: e?.reason ?? 'This table is no longer running.',
        tableId: e?.tableId ?? null,
      })
    const onConnect = () => {
      dispatch({ type: 'connected' })
      // Every reconnection door collapses to one: "put me back where I was".
      // Sent directly rather than through `send`, which refuses to emit while
      // the link is down — this is the message that ends that state.
      socket.emit(ClientEvent.Resume, {})
    }
    const onDisconnect = () => dispatch({ type: 'dropped', at: Date.now() })
    const onGesture = (g: GestureRelay) => receiveGesture(g)
    const onFelt = (t: FeltTouchRelay) => receiveFelt(t)
    setFeltSink((message) => {
      if (socket.connected) socket.volatile.emit(ClientEvent.FeltTouch, message)
    })
    // Volatile, and only while connected: a gesture is worth something now or
    // not at all, and must never be buffered and replayed after a blackout.
    setGestureSink((message) => {
      if (socket.connected) socket.volatile.emit(ClientEvent.Gesture, message)
    })

    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    socket.on(ServerEvent.Snapshot, onSnapshot)
    socket.on(ServerEvent.Error, onError)
    socket.on(ServerEvent.SessionInvalid, onSessionInvalid)
    socket.on(ServerEvent.Kicked, onKicked)
    socket.on(ServerEvent.Gesture, onGesture)
    socket.on(ServerEvent.FeltTouch, onFelt)
    if (socket.connected) onConnect()

    return () => {
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      socket.off(ServerEvent.Snapshot, onSnapshot)
      socket.off(ServerEvent.Error, onError)
      socket.off(ServerEvent.SessionInvalid, onSessionInvalid)
      socket.off(ServerEvent.Kicked, onKicked)
      socket.off(ServerEvent.Gesture, onGesture)
      socket.off(ServerEvent.FeltTouch, onFelt)
      setGestureSink(null)
      setFeltSink(null)
    }
  }, [])

  const send = useMemo<TableSend>(() => {
    // Nothing is sent once a link has existed and gone, or once the table is
    // over or the session dead. socket.io-client would otherwise buffer the
    // emit and flush it on reconnect: a tap made during an 8 s blackout arrived
    // 8.1 s later against a table that had moved on (VERDICT B7). A dropped tap
    // costs one tap. `canQueue` is the one exception — see connection.ts.
    const emit = (event: string, payload?: unknown) => {
      const state = connectionRef.current
      if (!canAct(state) && !canQueue(state)) return
      getSocket().emit(event, payload ?? {})
    }
    // Player actions go through the gate; lobby/administrative ones do not,
    // because a double "add bot" is harmless and gating them would make the
    // lobby feel stuck behind a 5 s timer if a snapshot is slow. They also go
    // out volatile, so one that is written in the window between the transport
    // dying and socket.io noticing is dropped rather than queued.
    const guarded = (event: string, payload?: unknown) => {
      const socket = getSocket()
      if (!socket.connected || !canAct(connectionRef.current)) return
      runGuarded(() => emitVolatile(socket, event, payload ?? {}))
    }
    return {
      createTable: (gameId, config) => emit(ClientEvent.CreateTable, { gameId, config }),
      joinTable: (code, seat) => emit(ClientEvent.JoinTable, { code, seat }),
      // Dropping the snapshot is part of leaving, not a nicety. The server
      // detaches the socket and takes the seat, so nothing further arrives for
      // that table — and the last snapshot would otherwise sit in state, which
      // is what made the lobby offer "Your table is waiting / Resume" for a
      // table the player had just walked away from, leading nowhere.
      leaveTable: () => {
        emit(ClientEvent.LeaveTable, {})
        setSnapshot(null)
      },
      chooseSeat: (seat) => emit(ClientEvent.ChooseSeat, { seat }),
      addBot: (seat, level) => emit(ClientEvent.AddBot, { seat, level }),
      removeBot: (seat) => emit(ClientEvent.RemoveBot, { seat }),
      updateConfig: (config) => emit(ClientEvent.UpdateConfig, { config }),
      setCoach: (allowed) => emit(ClientEvent.SetCoach, { allowed }),
      setListed: (listed) => emit(ClientEvent.SetListed, { listed }),
      startMatch: () => emit(ClientEvent.StartMatch, {}),
      rematchChoice: (isIn) => emit(ClientEvent.RematchChoice, { in: isIn }),
      call: (call) => guarded(ClientEvent.Call, { call }),
      decide: (decision) => guarded(ClientEvent.Decide, { decision }),
      play: (seat, card, nominate) => guarded(ClientEvent.Play, { seat, card, nominate }),
      ready: () => guarded(ClientEvent.Ready, {}),
      proposeReplacement: (seat) => emit(ClientEvent.ProposeReplacement, { seat }),
      voteReplacement: (seat, approve) => emit(ClientEvent.VoteReplacement, { seat, approve }),
      reclaimSeat: () => emit(ClientEvent.ReclaimSeat, {}),
      resume: (tableId) => emit(ClientEvent.Resume, { tableId }),
      requestSnapshot: () => emit(ClientEvent.RequestSnapshot, {}),
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])
  const acknowledgeEnd = useCallback(() => {
    // The table is gone on the server, so the snapshot of it is a picture of
    // something that no longer exists; drop it with the message.
    setSnapshot(null)
    dispatch({ type: 'acknowledged' })
  }, [])

  return {
    snapshot,
    connection,
    error,
    events: snapshot?.events ?? [],
    send,
    clearError,
    acknowledgeEnd,
  }
}

/** A volatile emit that is only dropped when the link is, not when the
 *  transport happens to be busy. socket.io discards a volatile packet whenever
 *  the transport is still writing the one before it — and a card dragged onto
 *  the table sends its last `gesture` in the same tick as the `play` it ends
 *  in, so every such play was thrown away (and the action gate then held the
 *  hand for its 5 s timeout). A write in progress is over by the next tick:
 *  wait for it rather than lose the action. */
export function emitVolatile(socket: Socket, event: string, payload: unknown): void {
  const engine = socket.io.engine
  if (!engine?.transport || engine.transport.writable) {
    socket.volatile.emit(event, payload)
    return
  }
  engine.once('drain', () => {
    if (socket.connected) socket.volatile.emit(event, payload)
  })
}
